import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { environment } from '@env/environment';

export type CallState = 'idle' | 'ringing' | 'connected' | 'ended';

export interface CallInfo {
  call_id: string;
  server_id: string;
  asterisk_ip: string;
  sip_user: string;
  caller: string;
  displayname: string;
  callee: string;
  jsep: RTCSessionDescriptionInit;
}

@Injectable({ providedIn: 'root' })
export class VoipService implements OnDestroy {
  private ws: WebSocket | null = null;
  private pc: RTCPeerConnection | null = null;
  private localStream: MediaStream | null = null;
  private currentCall: CallInfo | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private destroyed = false;

  readonly callState$ = new BehaviorSubject<CallState>('idle');
  readonly incomingCall$ = new BehaviorSubject<CallInfo | null>(null);
  readonly statusMessage$ = new BehaviorSubject<string>('');
  readonly remoteStream$ = new BehaviorSubject<MediaStream | null>(null);

  constructor() {
    this.connect();
  }

  private connect(): void {
    if (this.destroyed) return;
    try {
      this.ws = new WebSocket(environment.url_voip_ws);
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.ws.onopen = () => this.statusMessage$.next('VoIP conectado');
    this.ws.onerror = () => this.statusMessage$.next('Error VoIP');
    this.ws.onclose = () => {
      this.statusMessage$.next('');
      this.scheduleReconnect();
    };
    this.ws.onmessage = async ({ data }) => {
      try {
        await this.handleMessage(JSON.parse(data));
      } catch (e) {
        console.error('VoIP message error:', e);
      }
    };
  }

  private scheduleReconnect(): void {
    if (this.destroyed) return;
    this.reconnectTimer = setTimeout(() => this.connect(), 5000);
  }

  private async handleMessage(msg: any): Promise<void> {
    switch (msg.type) {
      case 'incoming_call':
        this.currentCall = msg as CallInfo;
        this.incomingCall$.next(this.currentCall);
        this.callState$.next('ringing');
        break;

      case 'trickle':
        if (!this.pc) return;
        try {
          const cand = (!msg.candidate || msg.candidate.completed || msg.candidate.candidate === '')
            ? null : msg.candidate;
          await this.pc.addIceCandidate(cand);
        } catch (e) {
          console.error('ICE candidate error:', e);
        }
        break;

      case 'call_ended':
        this.cleanup();
        this.callState$.next('ended');
        setTimeout(() => {
          if (this.callState$.value === 'ended') this.callState$.next('idle');
        }, 2000);
        break;
    }
  }

  async answerCall(): Promise<void> {
    if (!this.currentCall || this.ws?.readyState !== WebSocket.OPEN) return;

    this.pc = new RTCPeerConnection({
      iceServers: [
        { urls: 'turn:voip.next64.com:3478?transport=udp', username: 'next64turn', credential: environment.voip_turn_credential },
        { urls: 'turn:voip.next64.com:3478?transport=tcp', username: 'next64turn', credential: environment.voip_turn_credential },
      ],
      iceTransportPolicy: 'all',
    });

    this.pc.onconnectionstatechange = () => {
      const state = this.pc?.connectionState;
      if (!state) return;
      console.log('[voip] connection state:', state);
      this.statusMessage$.next(state);
      if (state === 'connected') this.callState$.next('connected');
      if (state === 'failed' || state === 'closed') this.hangup();
    };

    this.pc.onicecandidateerror = (e: any) => {
      console.warn('[voip] ICE candidate error:', e.errorCode, e.errorText, e.url);
    };

    this.pc.onicecandidate = ({ candidate }) => {
      if (candidate) {
        console.log('[voip] ICE candidate:', candidate.type, candidate.protocol, candidate.address ?? '(mdns)');
      } else {
        console.log('[voip] ICE gathering complete');
      }
      this.ws!.send(JSON.stringify({
        type: 'trickle',
        call_id: this.currentCall!.call_id,
        candidate: candidate ?? { completed: true },
      }));
    };

    this.pc.ontrack = ({ streams }) => {
      if (streams[0]) this.remoteStream$.next(streams[0]);
    };

    try {
      this.localStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
    } catch {
      this.statusMessage$.next('Sin acceso al micrófono');
      this.hangup();
      return;
    }

    this.localStream.getTracks().forEach(t => this.pc!.addTrack(t, this.localStream!));
    await this.pc.setRemoteDescription(this.currentCall.jsep);
    const answer = await this.pc.createAnswer();
    await this.pc.setLocalDescription(answer);

    this.ws!.send(JSON.stringify({
      type: 'accept_call',
      call_id: this.currentCall.call_id,
      jsep: { type: this.pc.localDescription!.type, sdp: this.pc.localDescription!.sdp },
    }));

    this.statusMessage$.next('Conectando...');
  }

  hangup(): void {
    if (this.currentCall && this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'hangup', call_id: this.currentCall.call_id }));
    }
    this.cleanup();
    this.callState$.next('idle');
  }

  private cleanup(): void {
    this.pc?.close();
    this.pc = null;
    this.localStream?.getTracks().forEach(t => t.stop());
    this.localStream = null;
    this.currentCall = null;
    this.incomingCall$.next(null);
    this.remoteStream$.next(null);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.cleanup();
    this.ws?.close();
  }
}
